/**
 * Builder Mode hand-off — carries the "build from description" intent from the
 * Builder Hub to the Builder Workspace.
 *
 * When the user clicks "Build it" on /builder, we navigate to
 * /builder/:project and want the persistent Ask-AI dock to kick off the full
 * first-generation pipeline (FRS → application.json → models → deploy) with the
 * supplied description — using the SAME dock UI that powers ongoing changes,
 * rather than bouncing to a different-looking page.
 *
 * sessionStorage (not a query param) is used so long descriptions don't bloat
 * the URL and the intent survives the navigation + dock remount cleanly.
 *
 * ATTACHMENTS are carried too (a PDF spec attached on the hub must reach the
 * build), but a 10 MB base64 PDF does not fit in sessionStorage. The hand-off
 * is a client-side navigate(), never a reload, so a module-scoped variable
 * survives it with no size limit; sessionStorage only mirrors small payloads
 * so an accidental F5 keeps them. On a hard reload with a large attachment the
 * description survives and the file doesn't — the build still starts.
 */
import type { WireAttachment } from '@/lib/attachments'

const KEY = 'twasta:builder-pending-build'
const ATT_KEY = 'twasta:builder-pending-attachments'
/** Max JSON length mirrored into sessionStorage (~1.5 MB of a ~5 MB quota). */
const SS_BUDGET = 1_500_000

interface PendingBuild {
  project: string
  description: string
  /** "Show me the plan before anything is built" — absent ⇒ true. */
  reviewFirst?: boolean
}

interface PendingAttachments {
  project: string
  items: WireAttachment[]
}

let memAttachments: PendingAttachments | null = null

export function setPendingBuild(
  project: string,
  description: string,
  attachments: WireAttachment[] = [],
  reviewFirst = true,
): void {
  memAttachments = attachments.length ? { project, items: attachments } : null
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ project, description, reviewFirst } as PendingBuild))
    sessionStorage.removeItem(ATT_KEY)
    if (attachments.length) {
      const payload = JSON.stringify({ project, items: attachments } as PendingAttachments)
      // Over budget → memory-only. The real (SPA navigation) flow still works.
      if (payload.length <= SS_BUDGET) sessionStorage.setItem(ATT_KEY, payload)
    }
  } catch {
    /* sessionStorage unavailable (private mode / quota) — memory still holds them */
  }
}

/** Returns the pending description for `project`, or null if there isn't one. */
export function getPendingBuild(project: string): string | null {
  try {
    const raw = sessionStorage.getItem(KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as PendingBuild
    return parsed.project === project ? parsed.description : null
  } catch {
    return null
  }
}

/** Whether the pending build should stop for the plan review (default true). */
export function getPendingReviewFirst(project: string): boolean {
  try {
    const raw = sessionStorage.getItem(KEY)
    if (!raw) return true
    const parsed = JSON.parse(raw) as PendingBuild
    return parsed.project !== project || parsed.reviewFirst !== false
  } catch {
    return true
  }
}

/** Attachments staged with the pending build. Memory first, storage fallback. */
export function getPendingAttachments(project: string): WireAttachment[] {
  if (memAttachments && memAttachments.project === project) return memAttachments.items
  try {
    const raw = sessionStorage.getItem(ATT_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as PendingAttachments
    return parsed.project === project && Array.isArray(parsed.items) ? parsed.items : []
  } catch {
    return []
  }
}

export function clearPendingBuild(): void {
  memAttachments = null
  try {
    sessionStorage.removeItem(KEY)
    sessionStorage.removeItem(ATT_KEY)
  } catch {
    /* ignore */
  }
}
